import { Injectable } from '@angular/core';
import * as c3 from 'c3';
import * as d3 from 'd3';

@Injectable({
  providedIn: 'root'
})
export class ChartDefinitionService {

  constructor() { }

  buildChart(selector: string | HTMLElement, title: string, height: number, width: number): c3.Chart {
    const def = {
      bindto: selector,
      data: {
          columns: [
              ['data1', 0],
              ['data2', 0],
              ['data3', 0],
              ['data4', 0]
          ],
          names: {
              data1: `Succeeded`,
              data2: `Failed`,
              data3: `In progress`,
              data4: `Other`,
          },
          colors: {
              data1: '#388e3c',
              data2: '#d32f2f',
              data3: '#4d8fea',
              data4: '#e0e0e0'
          },
          type : 'donut'
      },
      tooltip: {
        format: {
            value: d3.format(',')
        }
    },
      donut: {
          label: {
              format: () => ''
          },
          title: title
      },
      legend: {
          position: 'right'
      },
      size: {
          height: height,
          width: width
      }
    };
  
    return c3.generate(def);
  }

  featureFlagChart(selector: string | HTMLElement, height: number, width: number): c3.Chart {
    const def = {
      bindto: selector,
      data: {
        x: 'x',
        columns: [
          ['x', '-'],
          ['data1', '', 0],
          ['data2', '', 0],
          ['data3', '', 0]
        ],
        types: {
            data1: 'bar',
            data2: 'bar',
            data3: 'area-step'
        },
        groups: [
            ['data1', 'data2']
        ],
        names: {
            data1: 'Enabled', 
            data2: 'Disabled',
            data3: 'Old (Enabled > 21 days with no expiration date)'
        },
        order: null,
        colors: {
            data1: '#388e3c',
            data2: '#4d8fea',
            data3: '#d32f2f'
        }
      },
      bar: {
          width: 30
      },
      size: {
          width: width,
          height: height
      },
      axis: {
          x: {
              type: 'category'
          }
      }
    };
  
    return c3.generate(def);
  }

  activityChart1YWidget(selector: string | HTMLElement, title: string): c3.Chart {
    const def = this.activityChart1YDef(selector, title, 200, 300, '#99cc00');
    def.axis.x.tick.fit = false;
    def.axis.x.tick.format = (d) => { 
      const coeff = 1000 * 60 * 30;
      const date = new Date(Math.round(d.getTime() / coeff) * coeff)
      return d3.timeFormat('%H:%M')(date);
    };
    return c3.generate(def);
  }

  activityChart1Y(selector: string | HTMLElement, title: string): c3.Chart {
    const def = this.activityChart1YDef(selector, title, 200, undefined, '#99cc00');
    return c3.generate(def);
  }

  activityChart2YWidget(selector: string | HTMLElement, title: string): c3.Chart {
    const def = this.activityChart2YDef(selector, title, 200, 400, '');
    def.axis.x.tick.fit = false;
    def.axis.x.tick.format = (d) => { 
      const coeff = 1000 * 60 * 30;
      const date = new Date(Math.round(d.getTime() / coeff) * coeff)
      return d3.timeFormat('%H:%M')(date);
    };
    return c3.generate(def);
  }

  activityChart2Y(selector: string | HTMLElement, title: string): c3.Chart {
    const def = this.activityChart2YDef(selector, title, 200, undefined, '%I:%M %p');
    return c3.generate(def);
  }

  private activityChart1YDef(selector: string | HTMLElement, title: string, height: number, width: number, color: string) {
    const def = {
      bindto: selector,
      size: {
        height: height,
        width: width
    },
      data: {
        json: [{occurredOn: new Date()}],
        keys: {
          x: 'occurredOn',
          value: ['instances'],
        },
        xFormat: '%m/%d/%Y %H:%M:%S',
        type: 'area-spline',
        axes: {
          instances: 'y2'
        },
      },
      color: { pattern: [color] },
      grid: {
        y: { show: true }
      },
      axis: {
        y: {
          show: false,
          min: 0,
          padding: { top: 5, bottom: 0 },
          label: title
        },
        y2: {
          show: true,
          min: 0,
          padding: { top: 5, bottom: 0 },
          label: title,
          tick: {
            format: d3.format("d")
          }
        },
        x: {
          type: 'timeseries',
          tick: {
            fit: false,
            format: d3.timeFormat('%I:%M %p')
          }
        }
      },
      tooltip: {
        format: {
          title: function (d) {
            return d.toLocaleDateString() + ' ' + d.toLocaleTimeString();
          },
          value: function (value) {
            var format = d3.format(',');
            return format(value);
          }
        }
      }
    };
    return def;
  }
  private activityChart2YDef(selector: string | HTMLElement, title: string, height: number, width: number, xAxisFormat: string) {
    const def = {
      bindto: selector,
      size: {
          height: height,
          width: width
      },
      data: {
        json: [{occurredOn: new Date()}],
        keys: {
          x: 'occurredOn',
          value: ['errors', 'calls'],
        },
        xFormat: '%m/%d/%Y %H:%M:%S',
        type: 'area-spline',
        axes: {
          errors: 'y2',
          calls: 'y'
        },
      },
      color: { pattern: ['#ff6666', '#99cc00'] },
      grid: {
        y: { show: true }
      },
      axis: {
        y: {
          min: 0,
          padding: { top: 5, bottom: 0 },
          label: title,
          tick: {
            format: d3.format("d")
          }
        },
        y2: {
          show: true,
          min: 0,
          padding: { top: 5, bottom: 0 },
          label: 'Errors',
          tick: {
            format: d3.format("d")
          }
        },
        x: {
          type: 'timeseries',
          tick: {
            fit: false,
            format: (d) => { 
              return d3.timeFormat(xAxisFormat)(d);
            }
          }
        }
      },
      tooltip: {
        format: {
          title: function (d) {
            return d.toLocaleDateString() + ' ' + d.toLocaleTimeString();
          },
          value: function (value, ratio, id) {
            var format = d3.format(',');
            return format(value);
          }
        }
      }
    };
    return def;
  }
}